import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, RawAxiosResponseHeaders } from 'axios';

// Define the API configuration
interface APIConfig {
	baseURL?: string;
	timeout?: number;
	headers?: Record<string, string>;
}

// Define a generic API response interface
export interface APIResponse<T> {
	data: T;
	status: number;
	statusText: string;
	headers: RawAxiosResponseHeaders;
}

export interface APIError {
	code: string;
	message: string;
	data: { [ key: string ]: unknown } | null;
	status: number;
}

// Create the API wrapper class
export class APIClient {
	private client: AxiosInstance;

	constructor( config: APIConfig ) {
		this.client = axios.create( {
			baseURL: config.baseURL || 'https://public-api.wordpress.com',
			timeout: config.timeout || 10000, // Default timeout of 10 seconds
			headers: config.headers || {
				'Content-Type': 'application/json',
			},
		} );
	}

	// Generic GET method
	public async get<T>( url: string, config?: AxiosRequestConfig ): Promise<APIResponse<T>> {
		try {
			const response: AxiosResponse<T> = await this.client.get( url, config );
			return this.handleResponse( response );
		} catch ( error ) {
			throw this.handleError( error );
		}
	}

	// Generic POST method
	public async post<T, U>(
		url: string,
		data: U,
		config?: AxiosRequestConfig,
	): Promise<APIResponse<T>> {
		try {
			const response: AxiosResponse<T> = await this.client.post( url, data, config );
			return this.handleResponse( response );
		} catch ( error ) {
			throw this.handleError( error );
		}
	}

	// Generic PUT method
	public async put<T, U>(
		url: string,
		data: U,
		config?: AxiosRequestConfig,
	): Promise<APIResponse<T>> {
		try {
			const response: AxiosResponse<T> = await this.client.put( url, data, config );
			return this.handleResponse( response );
		} catch ( error ) {
			throw this.handleError( error );
		}
	}

	// Generic DELETE method
	public async delete<T>(
		url: string,
		config?: AxiosRequestConfig,
	): Promise<APIResponse<T>> {
		try {
			const response: AxiosResponse<T> = await this.client.delete( url, config );
			return this.handleResponse( response );
		} catch ( error ) {
			throw this.handleError( error );
		}
	}

	// Handle a response and return it in a standardized format.
	private handleResponse<T>( response: AxiosResponse<T> ): APIResponse<T> {
		return {
			data: response.data,
			status: response.status,
			statusText: response.statusText,
			headers: response.headers,
		};
	}

	// Handle an error and return it in a standardized format.
	private handleError( error: unknown ): APIError {
		if ( axios.isAxiosError( error ) ) {
			return {
				code: error?.response?.data?.code?.toString() || 'unknown',
				message: error?.response?.data?.message?.toString() || 'Unknown error.',
				data: error?.response?.data?.data || null,
				status: error?.status || 500,
			};
		}

		return {
			code: 'unknown',
			message: typeof error === 'object' && error !== null && 'message' in error && typeof error?.message === 'string' ? error.message : 'Unknown error',
			data: null,
			status: 500,
		};
	}
}

const wpcom = ( oauthToken: string ) =>
	new APIClient( {
		headers: {
			Authorization: `Bearer ${ oauthToken }`,
		},
	} );

export default wpcom;
